
import { h, Fragment, render } from 'preact'
import { useState, useEffect } from 'preact/hooks'
import { createPortal } from 'preact/compat'
import { useLocalStorage } from './hooks/useLocalStorage'
import { createModel, createUrl } from './utility/model'
import { naturalLanguageJoin } from './utility/naturalLanguageJoin'
import classnames from 'classnames'
import { json } from './utility/dom'
import { map } from './utility/object'
import { Job } from './components/Job'
import { Tag } from './components/Tag'


export function init() {
	
	const siteSettings = json('#site-settings')

	const queryFromFilters = filters => {
		return createModel({
			status: filters.status || '',
			specialism: filters.specialisms || [],
			region: '',
			type: filters.employmentType,
			engagement: '',
			ownerId: filters.ownerId,
			companyId: '',
			featured: '',
			completed: filters.completed,
			perPage: 4,
		})
	}

	// hide container if no roles
	const onHideEl = el => {
		if (el.getAttribute('data-hide-no-roles'))
			return () => el?.closest('.js-jobs-hide-no-roles')?.remove()
	}

	// gridded jobs
	const gridEls = [...document.querySelectorAll('.js-latest-jobs')]
	gridEls.map(el => {
		const filters = JSON.parse(el.getAttribute('data-filters') || '{}')
		render(createPortal(<LatestJobs query={queryFromFilters(filters)} siteSettings={siteSettings} onNoRoles={onHideEl(el)} />, el), el)
	})

	// dropdown module
	const dropdownEls = [...document.querySelectorAll('.js-dropdown-jobs')]
	dropdownEls.map(el => {
		const filters = JSON.parse(el.getAttribute('data-filters') || '{}')
		const hideWhenNoRoles = el.getAttribute('data-hide-no-roles')
		console.log(hideWhenNoRoles)
		render(createPortal(<DropdownJobs query={queryFromFilters(filters)} siteSettings={siteSettings} onNoRoles={onHideEl(el)} />, el), el)
	})
}


function useSearch({ query, siteSettings }) {
	const [results, setResults] = useState([])
	const [facets, setFacets] = useState({})
	const [pagination, setPagination] = useState({})
	const [loading, setLoading] = useState(false)
	const [first, setFirst] = useState(true)
	const [searchUrl, setSearchUrl] = useState('')
	const [viewAllText, setViewAllText] = useState('')

	const request = () => {
		const api = createUrl(siteSettings.homeUrl + '/api/jobs', query.getAll(), true)
		const search = createUrl(siteSettings.searchUrl, query.copy().remove('perPage').getAll(), false)
		setLoading(true)
		fetch(api).then(response => response.json()).then(response => {
			setSearchUrl(search)
			setLoading(false)
			setResults(response.data ?? [])
			setPagination(response.pagination ?? {})
			first && setFacets(response.facets ?? {})
			setFirst(false)
			setViewAllText(getViewAllText(query, response.facets, response.pagination))
		})
	}
	// first load
	useEffect(request, [])

	return { request, results, facets, pagination, loading, first, searchUrl, viewAllText }
}


function DropdownJobs({ query, siteSettings, onNoRoles }) {

	query.set('perPage', 3)

	const { 
		request, results, facets, 
		pagination, loading, first,
		searchUrl, viewAllText
	} = useSearch({ query, siteSettings })

	if (!first && !results.length) return onNoRoles()

	const total = pagination.totalItems
	const completed = (query.get('status') === 'completed')

	const onChange = event => {
		const value = event.currentTarget.value
		query.set('specialism', value ? [value] : [])
		request()
	}

	return <>
		<div class="filled-roles__select">
	        <label>
	            <select class="select--max-width" onChange={onChange}>
	            	<option value="">Select sector</option>
	            	<option disabled>—</option>
	            	{Object.values(facets.specialism || {}).map(facet => {
	            		const selected = query.hasItem('specialism', facet.value)
	            		if (!facet.count || facet.count < 1) return <></>
	            		return <option value={facet.value}>{facet.displayAs} ({facet.count || 0})</option>
	            	})}
	            </select>
	        </label>
	    </div>
	    <div style={loading ? 'opacity:0.5' : ''}>
	    	{results.map(result => {
	    		return <Job 
		    		job={result} 
		    		class="margin-bottom-2 bg--white" 
	    			small={true} 
	    			key={result.Id} 
	    			searchUrl={siteSettings.searchUrl} 
	    			showLink={!completed}
	    			showSave={!completed}
	    			showPosted={!completed}
	    			showEmploymentType={!completed}
	    			statusParam={completed ? query.get('status') : ''}
	    		/>
	    	})}
	    	{!results.length && <>
	    		<div class="box box-outline border--lightgrey rounded box-padding first-last">
	    			<h3 class="heading-5">No live vacancies found</h3>
	    			<p class="p--large">We’re adding roles all the time, so please check back later or <a href={siteSettings.registerUrl}>register with us</a> to be contacted in the future about suitable roles.</p>
	    		</div>
	    	</>}
	    		
	    </div>
	    {(pagination.totalItems > query.get('perPage')) && <>
			<a href={searchUrl} class="button--block button--outline button--yellow">{viewAllText}</a>
	    </>}
	</>
}


function LatestJobs({ query, siteSettings, onNoRoles }) {

	const { 
		request, results, facets, 
		pagination, loading, first,
		searchUrl, viewAllText
	} = useSearch({ query, siteSettings })	

	if (!first && !results.length) return onNoRoles()

	const total = pagination.totalItems
	const completed = (query.get('status') === 'completed')

	return <>
			<div class="button-row margin-bottom-3 margin-top-2 hide show@medium">
				{Object.values(facets.specialism || {}).map(facet => {
					if (!facet.count) return
					const selected = query.hasItem('specialism', facet.value)
					const onClick = () => {
						selected ? query.removeFrom('specialism', facet.value) : query.addTo('specialism', facet.value)
						request()
					}
					return <Tag 
						title={facet.displayAs}
						teal={selected} 
						grey={!selected}
						selected={selected} 
						count={facet.count}
						small
						onClick={onClick} 
					/>
				})}
			</div>

		<div class="grid grid--gutter--small margin-bottom-5" style={loading ? 'opacity:0.5' : ''}>
			{results.map(result => {
				return <div class="col-6@medium flex">
					<Job 
			    		job={result} 
		    			small={true} 
		    			key={result.Id} 
		    			searchUrl={siteSettings.searchUrl} 
		    			showLink={!completed}
		    			showSave={!completed}
		    			showPosted={!completed}
		    			showEmploymentType={!completed}
		    			statusParam={completed ? query.get('status') : ''}
		    		/>
				</div>
			})}
			{!results.length && <>
				<div class="col-12">
					<div class="box box-outline border--lightgrey rounded box-padding first-last">
						<h3 class="heading-5">No live vacancies found</h3>
						<p class="p--large">We’re adding roles all the time, so please check back later or <a href={siteSettings.registerUrl}>register with us</a> to be contacted in the future about suitable roles.</p>
					</div>
				</div>
			</>}
		</div>
	    {(pagination.totalItems > query.get('perPage')) && <>
			<a href={searchUrl} class="button--outline button--yellow">{viewAllText}</a>
	    </>}
	</>
}


function getViewAllText(query, facets, pagination) {

	const total = pagination.totalItems
	if (total && (total <= query.get('perPage'))) return ''
	
	const number = total ? total : ''
	const all = (total && (total > 10)) ? `all ${number}` : number
	const s = total === 1 ? '' : 's'
	const roles = `role${s}`
	
	let text = `View ${all} ${roles}`
	
	const specialisms = query.get('specialism').map(id => {
		return facets?.specialism?.[id]?.displayAs
	})
	
	if (specialisms.length === 1) text += ` in ${specialisms[0]}`
	if (specialisms.length >= 2 && specialisms.length < 4) text += ` in ${naturalLanguageJoin(specialisms)}`
	if (specialisms.length >= 4) text += ` in ${specialisms.length} categories`

	return text
}